import React, { useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { FontFamilies } from "@/constants/Typography";
import { Input } from "@/components/Input";
import { ActionButton } from "@/components/ActionButton";
import Icons from "@/assets/icons";
import { validateEmail } from "@/utils/validate";
import AuthServices from "@/services/authServices";
import { router } from "expo-router";
import useUserStore from "@/store/user";
import { storageUtils } from "@/utils/storage";
import { setGlobalSessionId } from "@/services";
import useOnboardStore from "@/store/onboard";
import { showError } from "@/utils/toast";
import { openURL } from "expo-linking";
import FBTrackingService from "@/services/fbTrackingService";
import { TRACKING_EVENTS } from "@/constants/trackingEvents";

const OnboardSlide16 = () => {
  const { referalId } = useOnboardStore();
  const { getUserProfile } = useUserStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>(
    {}
  );

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateAccount = async () => {
    if (validateForm() && agreedToTerms) {
      setLoading(true);
      console.log("[REFERRAL] Creating user account, referalId:", referalId || "none");
      try {
        const response = await AuthServices.createUser({
          email,
          password,
          referalId,
        });
        if (response.success) {
          setGlobalSessionId(response.data.sessionId);
          storageUtils.setSessionId(response.data.sessionId);
          const userProfile = await getUserProfile();

          if (userProfile?.id) {
            FBTrackingService.setUserId(userProfile.id);
          }

          FBTrackingService.trackEvent(TRACKING_EVENTS.USER_SIGNED_UP, {
            eventData: {
              email,
              user_id: userProfile?.id,
              has_referral: !!referalId,
            },
          });

          FBTrackingService.trackEvent(TRACKING_EVENTS.ONBOARDING_COMPLETED, {
            eventData: {
              email,
              user_id: userProfile?.id,
            },
          });

          if (userProfile && referalId) {
            FBTrackingService.trackSignUpWithReferral(
              {
                email,
                referalId,
              },
              {
                email: userProfile.email,
                userId: userProfile.id,
              }
            );
          }
          router.replace("/signup");
        } else {
          showError(response.message || "Failed to create account");
        }
      } catch {
        showError("An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    }
  };

  const isFormValid =
    email && password && agreedToTerms && Object.keys(errors).length === 0;

  return (
    <View style={styles.container}>
      {/* Fixed Logo at top */}
      <View style={styles.logoContainer}>
        <Icons.LogoWithTextBlack width={170} height={38} />
      </View>

      <KeyboardAvoidingView
        style={styles.keyboardView}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.content}>
            <View style={styles.header}>
              <Text style={styles.title}>Create an account</Text>
              <Text style={styles.subtitle}>
                Make an account to start filing claims.
              </Text>
            </View>

            <View style={styles.form}>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Email</Text>
                <View style={styles.inputWrapper}>
                  <Input
                    value={email}
                    onChangeText={setEmail}
                    placeholder="Email address"
                    placeholderTextColor="rgba(22, 29, 20, 0.3)"
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoComplete="email"
                    style={[styles.input, errors.email && styles.inputError]}
                    containerStyle={styles.inputContainer}
                  />
                </View>
                {errors.email && (
                  <Text style={styles.errorText}>{errors.email}</Text>
                )}
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Password</Text>
                <View style={styles.inputWrapper}>
                  <Input
                    value={password}
                    onChangeText={setPassword}
                    placeholder="Password"
                    placeholderTextColor="rgba(22, 29, 20, 0.3)"
                    secureTextEntry
                    style={[styles.input, errors.password && styles.inputError]}
                    containerStyle={styles.inputContainer}
                  />
                </View>
                {errors.password && (
                  <Text style={styles.errorText}>{errors.password}</Text>
                )}
              </View>

              <TouchableOpacity
                style={styles.checkboxRow}
                onPress={() => setAgreedToTerms(!agreedToTerms)}
              >
                <View
                  style={[styles.checkbox, agreedToTerms && styles.checkboxChecked]}
                >
                  {agreedToTerms && <Icons.Check width={10} height={10} />}
                </View>
                <Text style={styles.checkboxText}>
                  I agree to Sparrow&apos;s{" "}
                  <Text
                    style={styles.linkText}
                    onPress={() => {
                      openURL("https://usesparrow.com/terms");
                    }}
                  >
                    Terms of Use
                  </Text>
                  {" "}and{" "}
                  <Text
                    style={styles.linkText}
                    onPress={() => {
                      openURL("https://usesparrow.com/class-action-terms");
                    }}
                  >
                    Class Action Terms.
                  </Text>
                </Text>
              </TouchableOpacity>
            </View>

            {referalId ? (
              <View style={styles.referalIdContainer}>
                <Text style={styles.referalIdLabel}>Referral ID:</Text>
                <Text style={styles.referalIdValue}>{referalId}</Text>
              </View>
            ) : null}
          </View>
        </ScrollView>
      </KeyboardAvoidingView>

      <View style={styles.bottom}>
        <ActionButton
          title={loading ? "Creating account..." : "Create account"}
          onPress={handleCreateAccount}
          disabled={!isFormValid || loading}
          style={[styles.createButton, (!isFormValid || loading) && styles.createButtonDisabled]}
          icon={
            loading ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : undefined
          }
          iconPosition="right"
        />
      </View>
    </View>
  );
};

export default OnboardSlide16;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
  },
  logoContainer: {
    alignItems: "center",
    paddingTop: 32,
    paddingBottom: 24,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    flex: 1,
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  header: {
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
  },
  title: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 32,
    lineHeight: 38,
    color: "#161D14",
    textAlign: "center",
  },
  subtitle: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 16,
    lineHeight: 24,
    color: "#161D14",
    opacity: 0.7,
    textAlign: "center",
    letterSpacing: -0.32,
  },
  form: {
    gap: 12,
  },
  inputGroup: {
    gap: 6,
  },
  label: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 13,
    lineHeight: 19,
    color: "#161D14",
  },
  inputWrapper: {
    backgroundColor: "#F3F3F3",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#EBEBEB",
  },
  inputContainer: {
    marginBottom: 0,
  },
  input: {
    width: "100%",
    backgroundColor: "transparent",
    paddingHorizontal: 12,
    paddingVertical: 0,
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    height: 56,
    color: "#161D14",
    borderWidth: 0,
  },
  inputError: {
    borderWidth: 1,
    borderColor: "#FF4444",
  },
  errorText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 12,
    color: "#FF4444",
  },
  checkboxRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    marginTop: 0,
  },
  checkbox: {
    width: 16,
    height: 16,
    borderRadius: 2,
    borderWidth: 1,
    borderColor: "#161D14",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 3,
  },
  checkboxChecked: {
    backgroundColor: "#161D14",
  },
  checkboxText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    lineHeight: 23,
    color: "#161D14",
    flex: 1,
  },
  linkText: {
    color: "#0F9129",
  },
  bottom: {
    position: "absolute",
    bottom: -64,
    left: 0,
    right: 0,
    paddingHorizontal: 8,
  },
  createButton: {
    width: "100%",
    height: 56,
    borderRadius: 100,
  },
  createButtonDisabled: {
    opacity: 0.5,
  },
  referalIdContainer: {
    marginTop: 20,
    alignItems: "center",
  },
  referalIdLabel: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 13,
    color: "#161D14",
    opacity: 0.7,
  },
  referalIdValue: {
    fontFamily: FontFamilies.Inter.Bold,
    fontSize: 15,
    color: "#0F9129",
    marginTop: 2,
  },
});
